import { AppDestroy, provideAppDestroy } from '@bcf-vanilla-ts-v1-shared/di/app-destroy';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { transferRxNext } from '@bcf-vanilla-ts-v1-shared/misc/rx-helpers/transfer-rx-next';
import { BehaviorSubject, combineLatest, filter, from, map, mapTo, Observable, takeUntil } from 'rxjs';
import { mapToInstallBarVisibility } from './pure-utils';
import { PwaService, providePwaService } from './pwa.service';
import { type PwaDeviceContext, providePwaDeviceContext } from './pwa-device-context';
import { PwaMemory, providePwaMemory } from './pwa-memory';
import type { PwaDeviceInfo } from './types';

export class PwaManager {
  private _pwaInstallShownInstallBar$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _pwaInstallShownInstallPopup$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private _pwaService: PwaService,
    private _pwaMemory: PwaMemory,
    private _deviceTypeInfo: PwaDeviceContext,
    private _appDestroy: AppDestroy
  ) {
    this._init();
  }

  private _init(): void {
    if (!this._deviceTypeInfo.isMobile() || this._deviceTypeInfo.isPWA()) {
      return;
    }

    combineLatest([
      from(this._pwaMemory.shouldShowBar()),
      from(this._pwaMemory.shouldShowBarForIOS()),
      this._pwaService.isAppInstalled()
    ])
      .pipe(
        map(([shouldShowInstallBar, shouldShowPwaForIOS, isAppInstalled]: [boolean, boolean, boolean | undefined]) =>
          mapToInstallBarVisibility(
            [shouldShowInstallBar, shouldShowPwaForIOS, isAppInstalled],
            this._deviceTypeInfo.isIosDevice()
          )
        ),
        takeUntil(this._appDestroy.destroy$)
      )
      .subscribe(transferRxNext(this._pwaInstallShownInstallBar$));

    this._pwaService
      .isAppInstalled()
      .pipe(
        filter((isAppInstalled: boolean | undefined) => isAppInstalled === true),
        mapTo(false),
        takeUntil(this._appDestroy.destroy$)
      )
      .subscribe(transferRxNext(this._pwaInstallShownInstallPopup$));
  }

  public shouldShowBar(): Observable<boolean> {
    return this._pwaInstallShownInstallBar$;
  }

  public shouldShowPopup(): Observable<boolean> {
    return this._pwaInstallShownInstallPopup$;
  }

  public getPwaDeviceInfo(): PwaDeviceInfo {
    return {
      // biome-ignore lint/style/useNamingConvention: must be that way
      deviceOS: this._deviceTypeInfo.isIosDevice() ? 'ios' : 'android',
      browserType: this._deviceTypeInfo.isSafari() ? 'safari' : 'other'
    };
  }

  public install(): void {
    this._pwaInstallShownInstallBar$.next(false);

    if (this._deviceTypeInfo.isIosDevice()) {
      this._pwaInstallShownInstallPopup$.next(true);
      this._pwaMemory.setAsInstalledForIOS();
      return;
    }

    this._pwaService.install();
  }

  public dismiss(): void {
    this._pwaInstallShownInstallBar$.next(false);
    this._pwaMemory.setAsShownBar();
  }

  public closeInstall(): void {
    this._pwaInstallShownInstallPopup$.next(false);
  }
}

export function providePwaManager(): PwaManager {
  return provideSingleton(
    PwaManager,
    () => new PwaManager(providePwaService(), providePwaMemory(), providePwaDeviceContext(), provideAppDestroy())
  );
}
